import { isPast } from 'date-fns';
import type { EventRecordingStatus, ProgramEvent, ProgramEventListType } from 'models';
import type { ReactNode } from 'react';
import { Col, DataCardParticipants, Icon, Link, RadarLineIcon, Row, Tag, Text, Title, Tooltip } from 'ui';
import { useGetEventTypeTagProperties, useGetParticipantsCountLabel, useShouldShowLiveTag } from '../../hooks';
import { EventCanceledTitle } from '../event-canceled-title';
import { useGetEventTimeContent } from './hooks';

export type EventListItemType = {
  agendaTimeDisplay?: 'allDay' | 'endTime' | 'startTime';
  eventType: ProgramEventListType;
  recordingStatus?: EventRecordingStatus;
} & { isAllDay?: boolean } & Pick<
    ProgramEvent,
    'endTime' | 'goingInviteesCount' | 'id' | 'name' | 'startTime' | 'status'
  >;

type P = {
  event: EventListItemType;
  extra: ReactNode;
  getPathToEventDetail: (id: string) => string;
};

const EventListItem = ({
  event: {
    agendaTimeDisplay,
    endTime,
    eventType,
    goingInviteesCount,
    id,
    isAllDay,
    name,
    recordingStatus,
    startTime,
    status,
  },
  extra,
  getPathToEventDetail,
}: P) => {
  const getEventTypeTagProperties = useGetEventTypeTagProperties();
  const isPastEvent = isPast(endTime);
  const shouldShowLiveTag = useShouldShowLiveTag({ endTime, eventType, startTime, status });
  const isCanceled = status === 'canceled';
  const getParticipantsCountLabel = useGetParticipantsCountLabel({ eventType });
  const eventTimeContent = useGetEventTimeContent({
    agendaTimeDisplay,
    endTime,
    eventType,
    isAllDay,
    startTime,
    status,
  });

  return (
    <Link className="event-list-item" effects={{ hover: false }} to={getPathToEventDetail(id)}>
      <div className="event-list-item__content">
        <div className="event-list-item__content__start">
          <Row className="event-list-item__content__start--flex-column" gutter={[0, 'xs']}>
            <Col>{eventTimeContent}</Col>
            {shouldShowLiveTag ? (
              <Col>
                <Tag color="live" format={{ upperCase: true }} icon={<RadarLineIcon />}>
                  time_tag_live
                </Tag>
              </Col>
            ) : null}
          </Row>
        </div>
        <div>
          <Row gutter="xs">
            <Col span={24}>
              {isCanceled ? (
                <EventCanceledTitle title={{ children: name, ellipsis: { rows: 2, tooltip: true }, level: 4 }} />
              ) : (
                <Title ellipsis={{ rows: 2, tooltip: true }} level={4} marginBottom>
                  {name}
                </Title>
              )}
            </Col>
            <Col>
              <Tag {...getEventTypeTagProperties({ eventType })} />
            </Col>
            {isPastEvent && recordingStatus === 'recorded' ? (
              <Col>
                <Tooltip title="Recording available">
                  <Text>
                    <Icon icon="camcorder-line" />
                  </Text>
                </Tooltip>
              </Col>
            ) : null}

            <Col>
              <DataCardParticipants
                participants={{
                  count: goingInviteesCount,
                  label: getParticipantsCountLabel,
                }}
              />
            </Col>
          </Row>
        </div>
        <div className="event-list-item__content__end">{extra}</div>
      </div>
    </Link>
  );
};

export default EventListItem;
